import {
  type Column,
  type ColumnDef,
  type ColumnPinningState,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { Button, Card, CardContent, IconButton, Skeleton, SystemIcon, Typography } from '@volvo/vcdk-react';
import { SearchField } from '@volvo/vcdk-react/SearchField';
import { Table as TableVCDK } from '@volvo/vcdk-react/Table';
import { TableHeader } from '@volvo/vcdk-react/TableHeader';
import React, { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'underscore';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { analyticsEvents } from '@/contexts/analytics/events';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/utils/cn';

// These are the important styles to make sticky column pinning work!
const getCommonPinningStyles = <T,>(column: Column<T>): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '-2px 0 2px -2px gray inset'
      : isFirstRightPinnedColumn
        ? '2px 0 2px -2px gray inset'
        : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
    backgroundColor: isPinned ? '#ffffff' : 'var(--color-background-primary)',
  };
};

type SearchAnalyticsPayload = {
  numberTotal: number;
  numberPerPage: number;
};

const MobileCard = <T,>({
  data,
  columns,
  headerTitle,
  showSearch = true,
  actions,
  isLoading,
  showPagination = true,
  enableExpanding = false,
  expandingComponent,
  emptyState,
  onRowClick,
  pageIndex: externalPageIndex,
  onPageChange,
  globalFilter,
  onGlobalFilterChange,
  onSearchAnalyticsChange,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: externalPageIndex ? externalPageIndex - 1 : 0,
    pageSize: 6,
  });

  // Sync pagination from external pageIndex prop
  useEffect(() => {
    if (externalPageIndex !== undefined) {
      setPagination((prev) => ({ ...prev, pageIndex: externalPageIndex - 1 }));
    }
  }, [externalPageIndex]);

  // Handle pagination change and notify parent
  const handlePaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    setPagination((prev) => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      if (onPageChange && newState.pageIndex !== prev.pageIndex) {
        onPageChange(newState.pageIndex + 1);
      }
      return newState;
    });
  };

  // TanStack Table returns non-memoizable functions; React Compiler intentionally skips this.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowCanExpand: () => enableExpanding,
    onGlobalFilterChange: onGlobalFilterChange,
    onExpandedChange: setExpanded,
    onPaginationChange: handlePaginationChange,
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: (row, columnId, filterValue) => {
      const colFilterFn = row.getAllCells().find((c) => c.column.id === columnId)?.column.columnDef.filterFn;
      if (typeof colFilterFn === 'function') {
        return colFilterFn(row, columnId, filterValue, () => {});
      }
      const value = row.getValue(columnId);
      return String(value ?? '')
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    },
    state: {
      globalFilter,
      expanded,
      pagination,
    },
    manualPagination: false,
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const pageSize = table.getState().pagination.pageSize;

  useEffect(() => {
    onSearchAnalyticsChange?.({
      numberTotal: filteredRowCount,
      numberPerPage: pageSize,
    });
  }, [filteredRowCount, onSearchAnalyticsChange, pageSize]);

  // Scroll to top when page changes
  const pageIndex = table.getState().pagination.pageIndex;
  useEffect(() => {
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pageIndex]);

  return (
    <div ref={cardsContainerRef} className="flex flex-col gap-6">
      {!!showSearch || !!actions ? (
        <div className="flex items-center justify-between gap-6">
          {headerTitle && <Typography variant="heading4">{headerTitle}</Typography>}
          {!!actions && <div className="flex items-center gap-4 md:mb-4">{actions}</div>}
        </div>
      ) : null}
      {!!showSearch && (
        <div className="flex flex-1 items-center gap-4 md:mb-4">
          <SearchField
            size="medium"
            icon="search"
            className="w-full"
            value={globalFilter ?? ''}
            onInput={(e) => onGlobalFilterChange?.(e.detail.value)}
            onClickButton={() => onGlobalFilterChange?.('')}
            placeholder={t(($) => $.common.search)}
          />
        </div>
      )}

      {(data.length === 0 || filteredRowCount === 0) && !isLoading ? (
        <Card>
          <CardContent>
            <div className="flex justify-center py-8 text-center">
              {emptyState?.content ?? (
                <Typography variant="heading3">{emptyState?.title || 'No results found'}</Typography>
              )}
            </div>
          </CardContent>
        </Card>
      ) : isLoading ? (
        // Show skeleton cards while loading
        Array.from({ length: 3 }).map((_, idx) => (
          <Card key={`skeleton-card-${idx}`}>
            <CardContent>
              <div className="flex flex-col gap-3">
                <Skeleton width="60%" height="24px" />
                <Skeleton width="80%" height="16px" />
                <Skeleton width="40%" height="16px" />
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <>
          {table.getRowModel().rows.map((row, idx) => {
            // const hasActionsOrSelect = row
            //   .getVisibleCells()
            //   .some((cell) => cell.column.id === 'select' || cell.column.id === 'actions');

            return (
              <Card
                key={`${table.getState().pagination.pageIndex}-${row.id}-${idx}`}
                onClick={() => onRowClick?.(row.original)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                <CardContent>
                  <div className="relative flex flex-col gap-2">
                    {row
                      .getVisibleCells()
                      .sort((a, b) => {
                        const metaA = a.column.columnDef.meta as { order?: number } | undefined;
                        const metaB = b.column.columnDef.meta as { order?: number } | undefined;
                        const orderA = metaA?.order ?? 0;
                        const orderB = metaB?.order ?? 0;
                        return orderA - orderB;
                      })
                      .map((cell) => {
                        const meta = cell.column.columnDef.meta as
                          { hideOnMobile?: boolean; hideLabelOnMobile?: boolean } | undefined;

                        // Skip rendering if hideOnMobile is true
                        if (meta?.hideOnMobile) return null;

                        // Skip rendering the actions column in the card body, we'll add it separately
                        if (cell.column.id === 'select') {
                          return null;
                        }

                        if (cell.column.id === 'actions') {
                          return (
                            <div key={cell.id} className="" onClick={(e) => e.stopPropagation()}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          );
                        }
                        if (cell.column.id === 'expand') {
                          return (
                            <div key={cell.id} onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="tertiary"
                                size="small"
                                iconEnd={cell.row.getIsExpanded() ? 'chevron-up' : 'chevron-down'}
                                onClick={cell.row.getToggleExpandedHandler()}
                                className="w-full"
                              >
                                View details
                              </Button>
                              {expandingComponent && enableExpanding && row.getIsExpanded() ? (
                                <div className="">{expandingComponent?.(row.original)}</div>
                              ) : null}
                            </div>
                          );
                        }
                        return (
                          <div key={cell.id} className={cn('flex justify-between gap-1', {})}>
                            {meta?.hideLabelOnMobile ? null : (
                              <Typography variant="caption1" className="text-subtle font-semibold">
                                {typeof cell.column.columnDef.header === 'string'
                                  ? cell.column.columnDef.header
                                  : typeof cell.column.columnDef.header === 'function'
                                    ? cell.column.columnDef.header(
                                        cell
                                          .getContext()
                                          .table.getHeaderGroups()[0]
                                          .headers.find((h) => h.id === cell.column.id)!
                                          .getContext()
                                      )
                                    : cell.column.id}
                              </Typography>
                            )}
                            <div className="truncate">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {showPagination && filteredRowCount > 0 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="flex items-center gap-1">
                <Typography variant="caption1" className="text-subtle">
                  {t(($) => $.components.table.page, {
                    currentPage: table.getState().pagination.pageIndex + 1,
                    totalPages: table.getPageCount(),
                  })}
                </Typography>
              </div>
              <IconButton
                icon="chevron-left"
                size="extra-small"
                onClick={() => {
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              />
              <IconButton
                icon="chevron-right"
                size="extra-small"
                onClick={() => {
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DesktopCard = <T,>({
  data,
  columns,
  headerTitle,
  selectedActions,
  actions,
  isLoading,
  subRowsKey = 'subRows',
  showHeader = true,
  showSearch = true,
  defaultColumnPinning,
  defaultSorting = [],
  showPagination = true,
  enableExpanding = false,
  expandingComponent,
  columnVisibility: _columnVisibility,
  emptyState,
  initialRowSelection = {},
  onRowClick,
  onMouseOver,
  pageIndex: externalPageIndex,
  onPageChange,
  globalFilter,
  onGlobalFilterChange,
  onSearchAnalyticsChange,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>(initialRowSelection || {});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    defaultColumnPinning ?? {
      left: [],
      right: [],
    }
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(_columnVisibility || {});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: externalPageIndex ? externalPageIndex - 1 : 0,
    pageSize: showPagination ? 8 : 99999,
  });

  // Sync pagination from external pageIndex prop
  useEffect(() => {
    if (externalPageIndex !== undefined) {
      setPagination((prev) => ({ ...prev, pageIndex: externalPageIndex - 1 }));
    }
  }, [externalPageIndex]);

  // Handle pagination change and notify parent
  const handlePaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    setPagination((prev) => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      if (onPageChange && newState.pageIndex !== prev.pageIndex) {
        onPageChange(newState.pageIndex + 1);
      }
      return newState;
    });
  };

  // Check if table has horizontal overflow
  useEffect(() => {
    const checkOverflow = () => {
      if (tableContainerRef.current) {
        const hasScrollbar = tableContainerRef.current.scrollWidth > tableContainerRef.current.clientWidth;

        // Only apply pinning if there's overflow
        if (hasScrollbar && defaultColumnPinning) {
          setColumnPinning(defaultColumnPinning);
        } else if (!hasScrollbar) {
          setColumnPinning({ left: [], right: [] });
        }
      }
    };

    checkOverflow();

    // Recheck on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [defaultColumnPinning, data]);

  // TanStack Table returns non-memoizable functions; React Compiler intentionally skips this.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: () => enableExpanding,
    getSubRows: (row) => (row as Record<string, T[] | undefined>)[subRowsKey],
    onGlobalFilterChange: onGlobalFilterChange,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationChange,
    onColumnPinningChange: setColumnPinning,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    globalFilterFn: (row, columnId, filterValue) => {
      const colFilterFn = row.getAllCells().find((c) => c.column.id === columnId)?.column.columnDef.filterFn;
      if (typeof colFilterFn === 'function') {
        return colFilterFn(row, columnId, filterValue, () => {});
      }
      const value = row.getValue(columnId);
      return String(value ?? '')
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    },
    state: {
      columnVisibility,
      globalFilter,
      rowSelection,
      columnPinning,
      sorting,
      pagination,
      expanded,
    },
    manualPagination: false,
    debugTable: false,
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const pageSize = table.getState().pagination.pageSize;

  useEffect(() => {
    onSearchAnalyticsChange?.({
      numberTotal: filteredRowCount,
      numberPerPage: pageSize,
    });
  }, [filteredRowCount, onSearchAnalyticsChange, pageSize]);

  const selectedRowsCount = Object.keys(rowSelection).length;

  return (
    <TableVCDK>
      <TableHeader highlight={selectedRowsCount > 0} hidden={!showHeader} className="table-header">
        <div slot="start">
          <div>
            {headerTitle && <Typography variant="heading4">{headerTitle}</Typography>}
            {selectedRowsCount > 0 ? (
              <Typography variant="caption1" className="ml-4">
                {t(($) => $.components.table.items_selected, { selectedRowsCount })}
              </Typography>
            ) : null}
          </div>
        </div>
        <div slot="end">
          <div>
            {!!selectedActions && selectedRowsCount > 0 ? (
              selectedActions(
                table.getSelectedRowModel().rows.map((row) => row.original),
                () => setRowSelection({})
              )
            ) : (
              <div className="flex items-center gap-6">
                {showSearch && (
                  <SearchField
                    size="medium"
                    icon="search"
                    value={globalFilter ?? ''}
                    onInput={(e) => onGlobalFilterChange?.(e.detail.value)}
                    onClickButton={() => onGlobalFilterChange?.('')}
                    placeholder={t(($) => $.common.search)}
                  />
                )}
                {actions}
              </div>
            )}
          </div>
        </div>
      </TableHeader>
      {(data.length === 0 || filteredRowCount === 0) && !isLoading ? (
        <div className="flex justify-center pt-8 pb-10">
          {emptyState?.content ?? <Typography variant="heading3">{emptyState?.title || 'No results found'}</Typography>}
        </div>
      ) : (
        <>
          <div ref={tableContainerRef} className="flex-1 overflow-x-auto">
            <table
              className="w-full table-auto border-collapse"
              style={{
                minWidth: table.getAllColumns().reduce((sum, column) => sum + column.getSize(), 0),
              }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const meta = header.column.columnDef.meta as
                        | {
                            hideOnDesktop?: boolean;
                            order?: number;
                            textAlign?: 'right' | 'left' | 'center';
                          }
                        | undefined;
                      // Skip rendering header if hideOnDesktop is true
                      if (meta?.hideOnDesktop) return null;

                      const hasSortingFn = header.column.columnDef.sortingFn !== 'auto';
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          scope="col"
                          style={{ ...getCommonPinningStyles(header.column) }}
                        >
                          <div
                            className={cn('flex gap-4 whitespace-nowrap', {
                              'cursor-pointer select-none': hasSortingFn,
                              'justify-end': meta?.textAlign === 'right',
                              'justify-center': meta?.textAlign === 'center',
                              'justify-start': !meta?.textAlign || meta?.textAlign === 'left',
                            })}
                            onClick={hasSortingFn ? header.column.getToggleSortingHandler() : undefined}
                            title={
                              hasSortingFn
                                ? header.column.getNextSortingOrder() === 'asc'
                                  ? 'Sort ascending'
                                  : header.column.getNextSortingOrder() === 'desc'
                                    ? 'Sort descending'
                                    : 'Clear sort'
                                : undefined
                            }
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <SystemIcon icon="arrow-up" />,
                              desc: <SystemIcon icon="arrow-down" />,
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading
                  ? // Show skeleton rows while loading
                    Array.from({ length: 3 }).map((_, idx) => (
                      <tr key={`skeleton-${idx}`}>
                        {table.getAllColumns().map((column) => (
                          <td
                            key={`skeleton-${idx}-${column.id}`}
                            style={{
                              width: column.getSize(),
                            }}
                          >
                            <Skeleton
                              variant="rounded-rectangle"
                              width="100%"
                              height="24px"
                              style={{ marginBlockEnd: 0 }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  : // Show actual data when loaded
                    table.getRowModel().rows.map((row, idx) => {
                      return (
                        <React.Fragment key={row.id}>
                          <tr
                            key={`${table.getState().pagination.pageIndex}-${row.id}-${idx}`}
                            className="hover:bg-base"
                            style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                          >
                            {row.getVisibleCells().map((cell) => {
                              const meta = cell.column.columnDef.meta as
                                | {
                                    hideOnDesktop?: boolean;
                                    order?: number;
                                    textAlign?: 'right' | 'left' | 'center';
                                  }
                                | undefined;
                              // Skip rendering if hideOnDesktop is true
                              if (meta?.hideOnDesktop) return null;
                              return (
                                <td
                                  key={cell.id}
                                  onClick={() => (cell.id.includes('select') ? null : onRowClick?.(row.original))}
                                  onMouseOver={() => (cell.id.includes('select') ? null : onMouseOver?.(row.original))}
                                  style={{
                                    ...getCommonPinningStyles(cell.column),
                                    textAlign: meta?.textAlign,
                                  }}
                                  className="overflow-hidden text-ellipsis"
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                              );
                            })}
                          </tr>
                          {expandingComponent && enableExpanding && row.getIsExpanded() ? (
                            <tr className="bg-neutral-light">
                              <td
                                colSpan={row.getVisibleCells().length}
                                className=""
                                style={{
                                  paddingBlock: 16,
                                  paddingInline: 30,
                                }}
                              >
                                <div className="bg-white p-6">{expandingComponent?.(row.original)}</div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
              </tbody>
            </table>
          </div>
          {showPagination && filteredRowCount > 0 && (
            <div className="flex items-center justify-center gap-5 md:p-6">
              <div className="flex items-center gap-2">
                <Typography variant="caption1" className="text-subtle">
                  {t(($) => $.components.table.page, {
                    currentPage: table.getState().pagination.pageIndex + 1,
                    totalPages: table.getPageCount(),
                  })}
                </Typography>
              </div>
              <IconButton
                icon="chevron-left-last"
                size="extra-small"
                onClick={() => table.firstPage()}
                disabled={!table.getCanPreviousPage()}
              />
              <IconButton
                icon="chevron-left"
                size="extra-small"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />
              <IconButton
                icon="chevron-right"
                size="extra-small"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
              <IconButton
                icon="chevron-right-last"
                size="extra-small"
                onClick={() => table.lastPage()}
                disabled={!table.getCanNextPage()}
              />
            </div>
          )}
        </>
      )}
    </TableVCDK>
  );
};

type TableProps<T> = {
  isLoading: boolean;
  data: T[];
  columns: ColumnDef<T>[];
  headerTitle?: string;
  showHeader?: boolean;
  showSearch?: boolean;
  showPagination?: boolean;
  actions?: React.ReactNode;
  disableMobileView?: boolean;
  defaultColumnPinning?: ColumnPinningState;
  defaultSorting?: SortingState;
  enableExpanding?: boolean;
  subRowsKey?: string;
  expandingComponent?: (row: T) => React.ReactNode;
  columnVisibility?: VisibilityState | undefined;
  emptyState?: {
    title: string;
    content?: React.ReactNode;
  };
  initialRowSelection?: Record<number, boolean>;
  selectedActions?: (selectedRows: T[], clearSelection: () => void) => React.ReactNode;
  onRowClick?: (row: T) => void;
  onMouseOver?: (row: T) => void;
  pageIndex?: number;
  onPageChange?: (page: number) => void;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  onSearchAnalyticsChange?: (payload: SearchAnalyticsPayload) => void;
};

const Table = <T,>(props: TableProps<T>) => {
  const { mediaQueryNodes: mq } = useMediaQuery();
  const hasInitializedSearchAnalytics = useRef(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [searchAnalyticsPayload, setSearchAnalyticsPayload] = useState<SearchAnalyticsPayload | null>(null);

  const debouncedTrackSearch = useMemo(
    () =>
      debounce((term: string, numberTotal: number, numberPerPage: number, location: string) => {
        analyticsEvents.Search.siteSearch({
          term,
          numberTotal,
          numberPerPage,
          location,
        });
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedTrackSearch.cancel();
    };
  }, [debouncedTrackSearch]);

  useEffect(() => {
    if (!hasInitializedSearchAnalytics.current) {
      hasInitializedSearchAnalytics.current = true;
      return;
    }

    const term = globalFilter.trim();

    if (term.length < 3 || !searchAnalyticsPayload) {
      debouncedTrackSearch.cancel();
      return;
    }

    debouncedTrackSearch(
      term,
      searchAnalyticsPayload.numberTotal,
      searchAnalyticsPayload.numberPerPage,
      props.headerTitle || 'Table'
    );
  }, [debouncedTrackSearch, globalFilter, props.headerTitle, searchAnalyticsPayload]);

  const sharedProps = {
    ...props,
    globalFilter,
    onGlobalFilterChange: setGlobalFilter,
    onSearchAnalyticsChange: setSearchAnalyticsPayload,
  };

  if (props.disableMobileView) {
    return <DesktopCard {...sharedProps} />;
  }

  return mq({
    default: <MobileCard {...sharedProps} />,
    md: <DesktopCard {...sharedProps} />,
  });
};

export default withComponentErrorBoundary(Table) as typeof Table;
